import { KeyboardArrowUp as ArrowUp } from '@mui/icons-material';
import { Button, Collapse } from '@mui/material';
import LabeledCheckbox from 'components/general/inputs/LabeledCheckbox';
import LabeledDateTimePicker from 'components/general/inputs/LabeledPickers/LabeledDateTimePicker';
import { IClockConfig, IGuidanceCard } from 'components/general/types';
import EntitySegment from 'components/general/wizards/EntitySegment';
import useStyles from 'components/general/wizards/WizardSegment/styles';
import { useActiveEntities, useAppDispatch, useFormikForm, useSnackbar } from 'hooks';
import { SatelliteApi } from 'middleware/SatelliteApi/api';
import moment, { Moment } from 'moment';
import { BaseSyntheticEvent, useCallback, useMemo, useState } from 'react';
import { moment2Mjd } from 'utils/time';
import useGuidance from './guidance';
import validation from './validation';

interface IProps {
  index: string;
}
interface IForm {
  startTime: Moment | null;
  stopTime: Moment | null;
  realTime: boolean;
  syncTime: boolean;
}

const defaultValues: IForm = {
  startTime: null,
  stopTime: null,
  realTime: false,
  syncTime: false,
};

const TimeSegment = ({ index }: IProps) => {
  const { branch, clockConfig } = useActiveEntities();
  const filteredConfig = useMemo(
    () =>
      clockConfig
        ? {
            ...clockConfig,
            startTime: clockConfig?.startTime,
            stopTime: clockConfig?.stopTime,
            realTime: clockConfig?.realTime,
            syncTime: clockConfig?.syncTime,
          }
        : (defaultValues as IClockConfig),
    [clockConfig]
  );
  const { classes } = useStyles();
  const [loading, setLoading] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const {
    ClockConfig: {
      actions: { createClockConfig, updateClockConfig },
    },
  } = SatelliteApi;
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const successCallback = useCallback(() => {
    enqueueSnackbar('Time updated successfully', {
      variant: 'success',
    });
    setLoading(false);
  }, [enqueueSnackbar, setLoading]);

  const failureCallback = useCallback(
    (response: { error: { message: string } }) => {
      const errorMessage =
        response?.error?.message ||
        'Something went wrong. Please contact us if this issue persists.';
      enqueueSnackbar(errorMessage);
      setLoading(false);
    },
    [enqueueSnackbar, setLoading]
  );

  const { formik, guidance } = useFormikForm<IClockConfig, IForm>(
    defaultValues,
    (values: IForm) => {
      setLoading(true);
      dispatch(
        clockConfig === undefined
          ? createClockConfig({
              ...values,
              startTime: values.startTime,
              stopTime: values.stopTime,
              realTime: values.realTime,
              syncTime: values.syncTime,
              branchId: branch.id,
              type: 'ClockConfig',
              successCallback: (response: IClockConfig) => {
                dispatch(
                  updateClockConfig({
                    rootValues: { clockConfig: response.id },
                    branchId: branch.id,
                    successCallback,
                    failureCallback,
                  })
                );
              },
              failureCallback,
            })
          : updateClockConfig({
              ...values,
              startTime: values.startTime,
              stopTime: values.stopTime,
              realTime: values.realTime,
              syncTime: values.syncTime,
              id: clockConfig.id,
              branchId: branch.id,
              type: 'ClockConfig',
              successCallback,
              failureCallback,
            })
      );
    },
    validation,
    filteredConfig,
    { useGuidance, datetimes: ['startTime', 'stopTime'] }
  );

  const { handleSubmit, getFieldProps, resetForm, dirty, values, setFieldValue } = formik;

  return (
    <EntitySegment
      className="joyride-scenario-time"
      title="Time"
      index={index}
      // Guidance is optional for the formik hook, so we typecast it since it will be returned whenever we pass the hook useGuidance
      guidance={guidance as IGuidanceCard}
      onSubmit={handleSubmit}
      onReset={resetForm}
      disableSubmit={!dirty}
      loading={loading}
      xray={{
        ...clockConfig,
        ...values,
        startTime: moment.isMoment(values?.startTime)
          ? moment2Mjd(values.startTime)
          : clockConfig?.startTime,
        stopTime: moment.isMoment(values?.stopTime)
          ? moment2Mjd(values.stopTime)
          : clockConfig?.stopTime,
      }}
    >
      <div className={classes.inputs}>
        <LabeledDateTimePicker label="Start Date & Time (UTC)" {...getFieldProps('startTime')} />
        <LabeledDateTimePicker
          label="End Date & Time (UTC)"
          minDate={values.startTime as Moment}
          {...getFieldProps('stopTime')}
        />
      </div>
      <div className={classes.inputs}>
        <Button
          variant="outlined"
          style={{ width: '100%', marginTop: '0.75rem' }}
          onClick={() => setAdvancedOpen(!advancedOpen)}
        >
          <ArrowUp
            style={{
              transform: advancedOpen ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.25s ease',
            }}
          />
          Advanced
        </Button>
      </div>
      <Collapse in={advancedOpen}>
        <LabeledCheckbox
          lineHeight="20px"
          label="Real Time: Simulate at natural speed (1x performance)"
          {...getFieldProps('realTime')}
          formikOnChange={(e: BaseSyntheticEvent) => {
            if (!e.target?.checked) {
              setFieldValue('syncTime', false);
            }
          }}
        />
        {values.realTime && (
          <LabeledCheckbox
            lineHeight="20px"
            label="Sync Time: Catch up to wall-time, then run in step with real time"
            {...getFieldProps('syncTime')}
          />
        )}
      </Collapse>
    </EntitySegment>
  );
};

export default TimeSegment;
